import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SliderModule } from 'primeng/slider';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { Subject, takeUntil } from 'rxjs';

import { AudioPlayer } from '../audio-player/audio-player';
import { PropertiesEdit } from '../properties-edit/properties-edit';
import { VoicePickerComponent } from '../voice-picker/voice-picker.component';
import { ModelsService } from '@generated/services';
import { TestSttCommand, StartLlmTestCommand, StartLlmTestMessage, FileType, LanguageLocaleDto } from '@generated/models';
import { OpenApiPropertiesSchema } from '@generated/models/open-api-properties-schema';
import { LatestBenchmarkSnapshotDto } from '@generated/models/latest-benchmark-snapshot-dto';
import { dictToValueEntries, openApiSchemaToProperties, propertiesToOpenApiSchema, descriptorEntriesToDict } from '@shared/utils/openapi-schema-utils';
import { OpenapiPropertiesEditorComponent } from '@shared/components/openapi-properties-editor/openapi-properties-editor';
import { PropertyDefinition } from '@shared/models/property-schema';
import { Signalr } from '@core/services/signalr';
import { LanguagesService } from '@generated/services/languages.service';
import { FilesService } from '@generated/services/files.service';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  promptTokens?: number | null;
  completionTokens?: number | null;
  tokensPerSec?: number | null;
  ttftMs?: number | null;
  isStreaming?: boolean;
  failed?: boolean;
}

interface TtsHistoryItem {
  role: 'user' | 'assistant';
  text?: string;
  urls?: string[];
  generating?: boolean;
  failed?: boolean;
}

/** Rich emit from the tester drawer when the user commits a version. */
export interface TesterSelection {
  versionId: string;
  voiceId: string | null;
  additionalProperties: Record<string, any> | null;
  /** LLM-only: sampling temperature picked in the tester. null для non-LLM
   *  сессий и в случае disabled-toggle. */
  temperature: number | null;
  /** LLM-only: top-K sampling. null → бэк берёт дефолт schema/бенча. */
  topK: number | null;
  /** LLM-only: top-P sampling. null → дефолт. */
  topP: number | null;
  /** LLM-only: max output tokens (nullable integer). Ноль/null → без
   *  переопределения. */
  maxOutputTokens: number | null;
  /** Custom-LLM only (gateway code matches the caller's list): the
   *  OpenAPI schemas the user edited for the LLM endpoint's request
   *  headers and body. null когда tester не был в custom-LLM mode. */
  customHeadersSchema: OpenApiPropertiesSchema | null;
  customBodySchema: OpenApiPropertiesSchema | null;
}

interface SttHistoryItem {
  role: 'user' | 'assistant';
  audioUrl?: string;
  text?: string;
  transcribing?: boolean;
  failed?: boolean;
}

export interface TesterVersion {
  id: string;
  name: string;
  additionalProperties?: any | null;
  /** Snapshot последнего успешного benchmark run'а для показа
   *  метрик рядом с версией. Null / undefined — нет данных. */
  latestBenchmark?: LatestBenchmarkSnapshotDto | null;
}

@Component({
  selector: 'app-model-tester',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SliderModule,
    InputNumberModule,
    SelectModule,
    TooltipModule,
    AccordionModule,
    ToggleSwitchModule,
    OpenapiPropertiesEditorComponent,
    AudioPlayer,
    PropertiesEdit,
    VoicePickerComponent
  ],
  templateUrl: './model-tester.component.html',
  styleUrl: './model-tester.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModelTesterComponent implements OnInit, OnChanges {
  @Input({ required: true }) modelTypeCode!: string;
  @Input({ required: true }) versionId!: string;
  @Input() versions: TesterVersion[] = [];
  @Input() versionLocked: boolean = false;
  @Input() additionalProperties?: any | null;
  /**
   * Caller's currently-saved additional-property values for the initial
   * version. When the user reopens the tester for a model they've already
   * picked, we preload these instead of re-seeding from schema defaults —
   * otherwise tweaks "vanish" on reopen. Only applied while the active
   * version equals the initial versionId; switching versions falls back
   * to defaults so each schema gets its own seed.
   */
  @Input() currentValues?: Record<string, any> | null;
  /** Same idea for the TTS voice — pre-pick the saved voice on reopen. */
  @Input() currentVoiceId?: string | null;
  /** Initial value for the LLM temperature slider — used on first init
   *  and when the user reopens the tester for the same version. */
  @Input() currentTemperature?: number | null;
  /** Initial value for LLM top-K. undefined → не меняем, null → «дефолт»
   *  (disabled-toggle включён). */
  @Input() currentTopK?: number | null;
  /** Initial value for LLM top-P. */
  @Input() currentTopP?: number | null;
  /** Initial value for max output tokens. */
  @Input() currentMaxOutputTokens?: number | null;
  /**
   * Custom-LLM gateway: when on, the LLM branch grows two extra
   * editors for Headers + Body request schemas (OpenAPI), and the test
   * call ships filled values in StartLlmTestCommand.headers / .body.
   * Off (default) hides everything, behaviour unchanged for the
   * platform's curated LLM models.
   */
  @Input() allowCustomLlm: boolean = false;
  /** Initial schema for the custom LLM endpoint's request headers —
   *  edited live in the tester and emitted back on Select. */
  @Input() currentCustomHeadersSchema?: OpenApiPropertiesSchema | null;
  /** Initial schema for the custom LLM endpoint's request body. */
  @Input() currentCustomBodySchema?: OpenApiPropertiesSchema | null;
  @Input() defaultText?: string | null;
  @Input() languageId?: string | null;
  @Input() localeId?: string | null;
  /**
   * When true, render a large primary "Select" button below the test
   * controls. Clicking it commits the version + voice + tweaked
   * additional properties from the tester back to the parent.
   */
  @Input() allowSelect: boolean = false;
  @Output() versionSelected = new EventEmitter<TesterSelection>();
  /**
   * Показать ли admin-only кнопку «Edit properties» рядом с версией.
   * Дефолт false — обычные (не-админ) вызовы не должны видеть её. Админские
   * страницы (model-edit, language-locale-edit) явно передают true.
   */
  @Input() showPropertiesEdit = false;
  /**
   * "Edit properties" button — opens the model-level properties drawer for
   * the model that owns the currently shown version.
   */
  @Output() propertiesEditRequested = new EventEmitter<string>();

  /** Прокси над `selectedVersionSignal` — сохраняет старый API (ngModel в
   *  шаблоне через `[(ngModel)]="selectedTestVersionId"`), но под капотом
   *  всё едет через сигнал, к которому подписан fetch-effect. */
  get selectedTestVersionId(): string { return this.selectedVersionSignal(); }
  set selectedTestVersionId(v: string) { this.selectedVersionSignal.set(v ?? ''); }
  selectedTestVoiceId: string | null = null;

  private modelsService = inject(ModelsService);
  private filesService = inject(FilesService);
  private languagesService = inject(LanguagesService);
  private signalr = inject(Signalr);
  private cdr = inject(ChangeDetectorRef);

  private destroy$ = new Subject<void>();

  // Common
  isLoading = false;

  // Model parameters (additional_properties of the selected version), shared
  // by TTS/STT requests. Accordion is collapsed by default ([] = no open panel).
  parameters: any = {};
  parametersAccordionValue: string[] = [];

  // TTS
  ttsText = '';
  ttsHistory: TtsHistoryItem[] = [];

  // Языковая выпадашка — общая для TTS и STT
  languageOptions: { label: string; value: string; languageId?: string; localeId?: string }[] = [];
  selectedLanguageCode = '';

  // STT
  sttRecording = false;
  sttDraftBlob: Blob | null = null;     // запись или загруженный файл, ещё не отправленные
  sttDraftUrl: string | null = null;
  sttDraftFileName: string | null = null;
  sttHistory: SttHistoryItem[] = [];
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private recordedChunks: Blob[] = [];

  // LLM
  llmTemperature = 0.7;
  /** True → temperature is treated as `null` everywhere: slider is locked
   *  out, the LLM test payload omits it, and the `versionSelected` event
   *  carries `temperature: null` so the parent stores null on the row. */
  llmTemperatureDisabled = false;

  /** Top-K sampling — целое число ≥1. Disabled-toggle такой же паттерн
   *  что у temperature: значение null → «по умолчанию, бэк решает». */
  llmTopK = 40;
  llmTopKDisabled = true;
  llmTopP = 0.95;
  llmTopPDisabled = true;
  /** Max output tokens — nullable integer. Отдельный disable-toggle
   *  чтобы админ мог явно выставить «без лимита» (null) vs явное число. */
  llmMaxOutputTokens: number | null = null;
  llmMaxOutputTokensDisabled = true;

  // Custom LLM gateway state — schemas edited via OpenapiPropertiesEditor.
  // Defaults inside each property carry the per-field test value, and the
  // backend builds the actual outbound request from those.
  customHeadersProps: PropertyDefinition[] = [];
  customBodyProps: PropertyDefinition[] = [];
  customLlmAccordionValue: string[] = [];
  llmInput = '';
  llmMessages: ChatMessage[] = [];
  private currentStreamId: string | null = null;

  /** Initial versionId captured at construction; if the user later switches
   *  versions in the dropdown, we know the saved currentValues no longer
   *  apply and re-seed from schema defaults. */
  private initialVersionId: string = '';

  ngOnChanges(changes: SimpleChanges): void {
    // Reseed доп-параметров если schema изменилась (admin сохранил новую
    // model-level схему, caller пересёл versions/additionalProperties).
    const schemaShifted = (changes['versions'] && !changes['versions'].firstChange)
      || (changes['additionalProperties'] && !changes['additionalProperties'].firstChange);
    if (schemaShifted) this.reseedPreservingValues();

    // Прокидываем плейн @Input'ы versionId / versions в signal-источник,
    // на который подписан fetch-effect. Плейн @Input'ы оставлены как есть
    // — переписывать всю поверхность компонента ради одного сигнала
    // избыточно.
    if (
      changes['versionId'] || changes['versions']
    ) {
      this.selectedVersionSignal.set(this.pickInitialVersionId());
    }
  }

  private pickInitialVersionId(): string {
    const inList = new Set(this.versions.map(v => v.id));
    if (this.versionId && inList.has(this.versionId)) return this.versionId;
    return this.versions[0]?.id || this.versionId || '';
  }

  /** Источник истины для «какая версия сейчас в тестере» — на неё
   *  подписан effect, который триггерит GET /api/Models/versions/{id}.
   *  Ngmodel в шаблоне читает / пишет через `selectedTestVersionId`
   *  (см. геттер/сеттер ниже) чтобы не менять биндинги. */
  private readonly selectedVersionSignal = signal<string>('');

  constructor() {
    // Fetch schema по любой смене выбранной версии. Effect — реактивная
    // альтернатива ручным вызовам в ngOnInit/onTestVersionChange, живёт
    // всю жизнь компонента (auto-cleanup через DestroyRef).
    effect(() => {
      const id = this.selectedVersionSignal();
      if (id) this.loadVersionSchema(id);
    });
  }

  ngOnInit(): void {
    // Начальная версия — эффект над `selectedVersionSignal` сам подхватит
    // fetch по установке. `initialVersionId` фиксирует что "у нас было
    // при открытии" (используется в canSelect ниже).
    this.selectedTestVersionId = this.pickInitialVersionId();
    this.initialVersionId = this.selectedTestVersionId;
    if (this.isVoiceBased) {
      this.ttsText = this.defaultText || '';
      if (this.currentVoiceId) this.selectedTestVoiceId = this.currentVoiceId;
    }
    if (this.isStt || this.isVoiceBased) {
      this.loadLanguages();
    }
    if (this.isVad) {
      // VAD-тестер = единственная секция и есть — accordion со свойствами.
      // Держать его свёрнутым нет смысла (лишний клик), сразу открываем.
      this.parametersAccordionValue = ['params'];
    }
    if (this.isLlm) {
      this.subscribeLlmStream();
      // Pull through the agent's saved temperature on open; null means
      // "no override" and locks the slider via the disable toggle.
      if (this.currentTemperature === null) {
        this.llmTemperatureDisabled = true;
      } else if (typeof this.currentTemperature === 'number' && !Number.isNaN(this.currentTemperature)) {
        this.llmTemperature = this.currentTemperature;
        this.llmTemperatureDisabled = false;
      }
      // Top-K
      if (this.currentTopK === null) {
        this.llmTopKDisabled = true;
      } else if (typeof this.currentTopK === 'number' && !Number.isNaN(this.currentTopK)) {
        this.llmTopK = this.currentTopK;
        this.llmTopKDisabled = false;
      }
      // Top-P
      if (this.currentTopP === null) {
        this.llmTopPDisabled = true;
      } else if (typeof this.currentTopP === 'number' && !Number.isNaN(this.currentTopP)) {
        this.llmTopP = this.currentTopP;
        this.llmTopPDisabled = false;
      }
      // Max output tokens
      if (this.currentMaxOutputTokens === null) {
        this.llmMaxOutputTokensDisabled = true;
        this.llmMaxOutputTokens = null;
      } else if (typeof this.currentMaxOutputTokens === 'number' && !Number.isNaN(this.currentMaxOutputTokens)) {
        this.llmMaxOutputTokens = this.currentMaxOutputTokens;
        this.llmMaxOutputTokensDisabled = false;
      }
      if (this.allowCustomLlm) {
        this.customHeadersProps = this.parseSchemaToProperties(this.currentCustomHeadersSchema);
        this.customBodyProps = this.parseSchemaToProperties(this.currentCustomBodySchema);
      }
    }
    this.seedParametersFromSchema();
  }

  private parseSchemaToProperties(schema: OpenApiPropertiesSchema | null | undefined): PropertyDefinition[] {
    if (!schema) return [];
    try {
      return openApiSchemaToProperties(schema);
    } catch (e) {
      console.error('Failed to parse custom LLM schema:', e);
      return [];
    }
  }

  onCustomHeadersPropsChange(props: PropertyDefinition[]): void {
    this.customHeadersProps = props;
  }
  onCustomBodyPropsChange(props: PropertyDefinition[]): void {
    this.customBodyProps = props;
  }

  /** Serialise PropertyDefinition[] to OpenApiPropertiesSchema for the
   *  backend, or null when the schema would be empty. */
  private composeCustomSchema(props: PropertyDefinition[]): OpenApiPropertiesSchema | null {
    if (!props || props.length === 0) return null;
    return propertiesToOpenApiSchema(props) as OpenApiPropertiesSchema;
  }

  get versionPickerDisabled(): boolean {
    return this.versionLocked || this.versions.length <= 1;
  }

  get currentAdditionalProperties(): any | null {
    // Fetch-first: если тестер уже подгрузил свежую версию — рисуем её
    // schema. Fallback на legacy `additionalProperties` @Input и на
    // preloaded `TesterVersion.additionalProperties` — для обратной
    // совместимости с caller'ами, которые сами всё ещё сеют схему.
    if (this.selectedTestVersionId && this.schemaVersionId() === this.selectedTestVersionId && this.schemaState() === 'ready') {
      return this.schemaData();
    }
    if (!this.selectedTestVersionId) return this.additionalProperties ?? null;
    const v = this.versions.find(x => x.id === this.selectedTestVersionId);
    return v?.additionalProperties ?? this.additionalProperties ?? null;
  }

  // === Version schema fetch (SKN-313) ===
  // GET /api/Models/versions/{id} возвращает `ModelVersionFullDto` с
  // additional_properties на трёх уровнях (ModelType → Model → Version).
  // Раньше мы их мержили, сейчас — берём ТОЛЬКО version-уровень. Схемы
  // ModelType/Model остаются как «справочная информация» бэка, но в
  // UI не участвуют — юзер редактирует и видит только то, что явно
  // определено на самой версии. Cache: последний успешный versionId +
  // его схема — при повторном открытии тестера не бомбим сеть.
  readonly schemaState = signal<'idle' | 'loading' | 'ready' | 'error'>('idle');
  readonly schemaVersionId = signal<string>('');
  readonly schemaData = signal<{ [key: string]: any } | null>(null);
  readonly schemaError = signal<string | null>(null);

  loadVersionSchema(versionId: string, force = false): void {
    if (!versionId) return;
    if (!force && this.schemaVersionId() === versionId && this.schemaState() === 'ready') return;
    this.schemaState.set('loading');
    this.schemaError.set(null);
    this.schemaVersionId.set(versionId);
    this.modelsService.apiModelsVersionsModelVersionIdGet({ modelVersionId: versionId })
      .subscribe({
        next: (full) => {
          if (this.schemaVersionId() !== versionId) return; // stale
          this.schemaData.set(this.versionOnlySchema(full));
          this.schemaState.set('ready');
          this.seedParametersFromSchema();
          this.cdr.markForCheck();
        },
        error: (err) => {
          if (this.schemaVersionId() !== versionId) return;
          this.schemaError.set(err?.message ?? 'Failed to load version schema');
          this.schemaState.set('error');
          this.cdr.markForCheck();
        }
      });
  }

  retryVersionSchema(): void {
    if (this.selectedTestVersionId) this.loadVersionSchema(this.selectedTestVersionId, true);
  }

  /** additional_properties ТОЛЬКО с уровня ModelVersion. Ранее мержили
   *  ModelType → Model → Version, но такой мерж сбивал с толку (юзер
   *  видел поля, которые ни к его модели, ни к его версии явно не
   *  относились). Берём только `full.additional_properties.properties`
   *  и конвертим в dict через descriptorEntriesToDict. */
  private versionOnlySchema(full: import('@generated/models/model-version-full-dto').ModelVersionFullDto): { [key: string]: any } {
    return descriptorEntriesToDict(full.additional_properties?.properties);
  }

  /** Snapshot последнего успешного бенчмарка выбранной версии — null если
   *  версия ни разу не бенчилась или caller не прокинул данные. */
  get currentLatestBenchmark(): LatestBenchmarkSnapshotDto | null {
    if (!this.selectedTestVersionId) return null;
    const v = this.versions.find(x => x.id === this.selectedTestVersionId);
    return v?.latestBenchmark ?? null;
  }

  get currentBenchmarkTypeCode(): string {
    return (this.currentLatestBenchmark?.model_type_code || '').toLowerCase();
  }

  formatBenchmarkAt(value: string | null | undefined): string {
    if (!value) return '';
    try { return new Date(value).toLocaleString(); } catch { return value; }
  }
  formatTokensPerSec(value: number | null | undefined): string {
    return value == null ? '—' : `${value.toFixed(1)} t/s`;
  }
  formatTtftMs(value: number | null | undefined): string {
    return value == null ? '—' : `${Math.round(value)} ms`;
  }
  formatErrorRate(value: number | null | undefined): string {
    return value == null ? '—' : `${(value * 100).toFixed(1)}%`;
  }
  formatRtfx(value: number | null | undefined): string {
    return value == null ? '—' : `${value.toFixed(2)}×`;
  }

  onTestVersionChange(): void {
    // Схема свойств у новой версии подтянется через effect() над
    // selectedVersionSignal — [(ngModel)] уже обновил его. Здесь только
    // сброс голоса (принадлежит предыдущей TTS-версии).
    this.selectedTestVoiceId = null;
    this.cdr.markForCheck();
  }

  onTestVoiceChange(voiceId: string | null): void {
    this.selectedTestVoiceId = voiceId;
    this.cdr.markForCheck();
  }

  onSelectVersionClick(): void {
    if (!this.canSelect) return;
    const params = this.parameters ?? null;
    const hasParams = params && Object.keys(params).length > 0;
    const customHeadersSchema = this.isLlm && this.allowCustomLlm
      ? (propertiesToOpenApiSchema(this.customHeadersProps) as OpenApiPropertiesSchema)
      : null;
    const customBodySchema = this.isLlm && this.allowCustomLlm
      ? (propertiesToOpenApiSchema(this.customBodyProps) as OpenApiPropertiesSchema)
      : null;
    this.versionSelected.emit({
      versionId: this.selectedTestVersionId,
      // Voice picker рендерится для TTS и Realtime. Для остальных типов null.
      voiceId: this.isVoiceBased ? (this.selectedTestVoiceId ?? null) : null,
      additionalProperties: hasParams ? { ...params } : null,
      // Sampling params: только для LLM и только если disable-toggle
      // выключен. null = «используем дефолт бэка / бенча».
      temperature: this.isLlm && !this.llmTemperatureDisabled ? this.llmTemperature : null,
      topK: this.isLlm && !this.llmTopKDisabled ? this.llmTopK : null,
      topP: this.isLlm && !this.llmTopPDisabled ? this.llmTopP : null,
      maxOutputTokens: this.isLlm && !this.llmMaxOutputTokensDisabled ? this.llmMaxOutputTokens : null,
      customHeadersSchema,
      customBodySchema
    });
  }

  /** Properties-editor button — forward the active version id so the
   *  parent can resolve back to the owning model. Visibility controlled
   *  by [showPropertiesEdit] input (admin pages set it to true). */
  onPropertiesEditClick(): void {
    if (!this.selectedTestVersionId) return;
    this.propertiesEditRequested.emit(this.selectedTestVersionId);
  }

  /**
   * Public guard for the drawer-footer Select button — TTS additionally
   * requires a voice; other types just need a version.
   */
  get canSelect(): boolean {
    if (!this.selectedTestVersionId) return false;
    if (this.isVoiceBased && !this.selectedTestVoiceId) return false;
    return true;
  }

  onParametersChange(params: any): void {
    this.parameters = params;
  }

  /**
   * Schema changed mid-session — keep whatever the user already typed for
   * keys that still exist, seed defaults for new keys, drop anything the
   * removed properties left behind. Used by ngOnChanges when the admin
   * properties drawer pushes a fresh schema down without recreating us.
   */
  private reseedPreservingValues(): void {
    const schema = this.currentAdditionalProperties;
    const seeded: Record<string, any> = {};
    if (schema) {
      for (const [name, descriptor] of Object.entries<any>(schema)) {
        const existing = this.parameters?.[name];
        seeded[name] = existing !== undefined
          ? existing
          : (descriptor?.default !== undefined ? descriptor.default : this.defaultForType(descriptor?.type));
      }
    }
    this.parameters = seeded;
    this.cdr.markForCheck();
  }

  /**
   * app-properties-edit заполняет дефолты внутри, но эмитит valuesChange
   * только на ручное изменение. Чтобы в запрос уходили дефолты даже без
   * правок юзером — сами сидируем parameters из схемы (descriptor.default).
   * Если родитель передал currentValues и активна та же версия, что была
   * на старте — оверлеем сохранённые значения поверх дефолтов, чтобы
   * повторный заход в тестер показывал то, что юзер уже выбрал.
   */
  private seedParametersFromSchema(): void {
    const schema = this.currentAdditionalProperties;
    const seeded: Record<string, any> = {};
    if (schema) {
      for (const [name, descriptor] of Object.entries<any>(schema)) {
        seeded[name] = descriptor?.default !== undefined
          ? descriptor.default
          : this.defaultForType(descriptor?.type);
      }
    }
    const useSaved = this.currentValues
      && this.selectedTestVersionId
      && this.selectedTestVersionId === this.initialVersionId;
    if (useSaved) {
      for (const [name, value] of Object.entries(this.currentValues!)) {
        if (value !== undefined) seeded[name] = value;
      }
    }
    this.parameters = seeded;
  }

  private defaultForType(type?: string): any {
    switch (type) {
      case 'string': return '';
      case 'integer':
      case 'number': return 0;
      case 'boolean': return false;
      case 'array': return [];
      default: return null;
    }
  }

  ngOnDestroy(): void {
    this.stopMicStream();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isTts(): boolean { return this.modelTypeCode?.toLowerCase() === 'tts'; }
  get isStt(): boolean { return this.modelTypeCode?.toLowerCase() === 'stt'; }
  get isLlm(): boolean { return this.modelTypeCode?.toLowerCase() === 'llm'; }
  get isVad(): boolean { return this.modelTypeCode?.toLowerCase() === 'vad'; }
  get isRealtime(): boolean { return this.modelTypeCode?.toLowerCase() === 'realtime'; }
  /** Voice-based тестер (голоса + text→audio) — TTS и Realtime используют
   *  один и тот же UI: язык, voice-picker, ввод текста → audio. Бэк по
   *  voice_id найдёт правильную версию (tts или realtime) и вернёт аудио. */
  get isVoiceBased(): boolean { return this.isTts || this.isRealtime; }

  // ========== TTS ==========
  async generateTts(): Promise<void> {
    const text = this.ttsText.trim();
    if (!text || this.isLoading) return;

    const userItem: TtsHistoryItem = { role: 'user', text };
    const assistantItem: TtsHistoryItem = { role: 'assistant', generating: true };
    this.ttsHistory = [...this.ttsHistory, userItem, assistantItem];
    // Текст в инпуте НЕ обнуляем — юзер часто прогоняет один и тот же
    // текст через разные голоса / параметры, дёргать заново не хочется.
    this.isLoading = true;
    this.cdr.markForCheck();

    const opt = this.languageOptions.find(o => o.value === this.selectedLanguageCode);
    const langId = opt?.languageId ?? this.languageId ?? undefined;
    const localeId = opt?.localeId ?? this.localeId ?? undefined;

    if (!this.selectedTestVoiceId) {
      assistantItem.generating = false;
      assistantItem.failed = true;
      this.ttsHistory = [...this.ttsHistory];
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    try {
      const result = await this.modelsService.apiModelsTtsTestPost({
        body: {
          voice_id: this.selectedTestVoiceId,
          text,
          parameters: this.parameters || {},
          language_id: langId,
          locale_id: localeId
        }
      }).toPromise();
      assistantItem.generating = false;
      assistantItem.urls = result?.download_urls || [];
      this.ttsHistory = [...this.ttsHistory];
    } catch (e) {
      console.error('TTS test failed', e);
      assistantItem.generating = false;
      assistantItem.failed = true;
      this.ttsHistory = [...this.ttsHistory];
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  resetTts(): void {
    this.ttsHistory = [];
    this.ttsText = '';
    this.cdr.markForCheck();
  }

  onTtsInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.generateTts();
    }
  }

  // ========== STT ==========
  get languageDisabled(): boolean {
    return !!this.languageId && !!this.localeId;
  }

  private loadLanguages(): void {
    this.languagesService.apiLanguagesLanguageLocalesGet().pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        const locales = result?.language_locales || [];
        this.languageOptions = locales
          .filter((l: LanguageLocaleDto) => l.is_active && l.full_code)
          .map((l: LanguageLocaleDto) => ({
            label: `${l.locale_flag || ''} ${l.language_name} - ${l.locale_name}`,
            value: l.full_code || '',
            languageId: l.language_id,
            localeId: l.locale_id
          }));
        // Pre-select from input if provided, иначе берём первый доступный язык
        let preset = '';
        if (this.languageId && this.localeId) {
          const found = this.languageOptions.find(o => o.languageId === this.languageId && o.localeId === this.localeId);
          if (found) preset = found.value;
        }
        this.selectedLanguageCode = preset || this.languageOptions[0]?.value || '';
        this.cdr.markForCheck();
      }
    });
  }

  async startRecording(): Promise<void> {
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.recordedChunks = [];
      this.clearDraft();
      this.mediaRecorder = new MediaRecorder(this.mediaStream, { mimeType: 'audio/webm;codecs=opus' });
      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this.recordedChunks.push(e.data);
      };
      this.mediaRecorder.onstop = () => {
        this.sttDraftBlob = new Blob(this.recordedChunks, { type: 'audio/webm' });
        this.sttDraftUrl = URL.createObjectURL(this.sttDraftBlob);
        this.sttDraftFileName = `stt-test-${Date.now()}.webm`;
        this.stopMicStream();
        this.cdr.markForCheck();
      };
      this.mediaRecorder.start();
      this.sttRecording = true;
      this.cdr.markForCheck();
    } catch (e) {
      console.error('Mic access failed', e);
    }
  }

  stopRecording(): void {
    this.mediaRecorder?.stop();
    this.sttRecording = false;
    this.cdr.markForCheck();
  }

  private stopMicStream(): void {
    this.mediaStream?.getTracks().forEach(t => t.stop());
    this.mediaStream = null;
  }

  onSttFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.clearDraft();
    if (file) {
      this.sttDraftBlob = file;
      this.sttDraftUrl = URL.createObjectURL(file);
      this.sttDraftFileName = file.name;
    }
    this.cdr.markForCheck();
  }

  discardDraft(): void {
    this.clearDraft();
    this.cdr.markForCheck();
  }

  private clearDraft(): void {
    this.sttDraftBlob = null;
    this.sttDraftUrl = null;
    this.sttDraftFileName = null;
  }

  get sttCanSend(): boolean {
    return !!this.sttDraftBlob && !!this.selectedLanguageCode && !this.isLoading;
  }

  async sendStt(): Promise<void> {
    if (!this.sttDraftBlob || !this.selectedLanguageCode || this.isLoading) return;

    const opt = this.languageOptions.find(o => o.value === this.selectedLanguageCode);
    if (!opt) return;

    const blob = this.sttDraftBlob;
    const fileName = this.sttDraftFileName || `stt-test-${Date.now()}.webm`;
    const contentType = blob.type || 'audio/webm';
    const audioUrl = this.sttDraftUrl || URL.createObjectURL(blob);

    const userItem: SttHistoryItem = { role: 'user', audioUrl };
    const assistantItem: SttHistoryItem = { role: 'assistant', transcribing: true };
    this.sttHistory = [...this.sttHistory, userItem, assistantItem];
    this.clearDraft();
    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      const uploadInfo = await this.filesService.apiFilesGenerateUploadUrlPost({
        body: { file_name: fileName, content_type: contentType, file_type: 'SttTestAudio' as FileType }
      }).toPromise();

      if (!uploadInfo?.upload_url || !uploadInfo?.file_id) {
        throw new Error('Failed to get upload URL');
      }

      const putRes = await fetch(uploadInfo.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: blob
      });
      if (!putRes.ok) throw new Error(`Upload failed: ${putRes.status}`);

      const command: TestSttCommand = {
        stt_version_id: this.selectedTestVersionId,
        file_id: uploadInfo.file_id,
        language_id: opt.languageId,
        locale_id: opt.localeId,
        // Бэк переименовал parameters (dict) в additional_properties
        // (Array<OpenApiPropertyValueEntry>) — общий shape со всеми
        // остальными test-command'ами. dictToValueEntries уже возвращает
        // нужный массив.
        additional_properties: dictToValueEntries(this.parameters),
      };

      const result = await this.modelsService.apiModelsSttTestPost({ body: command }).toPromise();
      assistantItem.transcribing = false;
      assistantItem.text = result?.text || '';
      this.sttHistory = [...this.sttHistory];
    } catch (e) {
      console.error('STT test failed', e);
      assistantItem.transcribing = false;
      assistantItem.failed = true;
      this.sttHistory = [...this.sttHistory];
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  resetStt(): void {
    this.sttHistory = [];
    this.clearDraft();
    this.cdr.markForCheck();
  }

  // ========== LLM ==========
  private subscribeLlmStream(): void {
    this.signalr.llmStreamChunk$.pipe(takeUntil(this.destroy$)).subscribe(msg => {
      if (msg.stream_id !== this.currentStreamId) return;
      const last = this.llmMessages[this.llmMessages.length - 1];
      if (last && last.role === 'assistant' && last.isStreaming) {
        last.content += msg.delta || '';
        this.llmMessages = [...this.llmMessages];
        this.cdr.markForCheck();
      }
    });
    this.signalr.llmStreamCompleted$.pipe(takeUntil(this.destroy$)).subscribe(msg => {
      if (msg.stream_id !== this.currentStreamId) return;
      const last = this.llmMessages[this.llmMessages.length - 1];
      if (last && last.role === 'assistant') {
        last.isStreaming = false;
        last.promptTokens = msg.prompt_tokens;
        last.completionTokens = msg.completion_tokens;
        last.tokensPerSec = msg.tokens_per_sec;
        last.ttftMs = msg.ttft_ms;
        this.llmMessages = [...this.llmMessages];
      }
      this.currentStreamId = null;
      this.isLoading = false;
      this.cdr.markForCheck();
    });
    this.signalr.llmStreamFailed$.pipe(takeUntil(this.destroy$)).subscribe(msg => {
      if (msg.stream_id !== this.currentStreamId) return;
      const last = this.llmMessages[this.llmMessages.length - 1];
      if (last && last.role === 'assistant') {
        last.isStreaming = false;
        last.failed = true;
        last.content = msg.error_message || $localize`:@@modelTester.llm.error:LLM call failed`;
        this.llmMessages = [...this.llmMessages];
      }
      this.currentStreamId = null;
      this.isLoading = false;
      this.cdr.markForCheck();
    });
  }

  async sendLlm(): Promise<void> {
    const text = this.llmInput.trim();
    if (!text || this.isLoading) return;

    this.llmMessages = [
      ...this.llmMessages,
      { role: 'user', content: text },
      { role: 'assistant', content: '', isStreaming: true }
    ];
    this.llmInput = '';
    this.isLoading = true;
    this.cdr.markForCheck();

    const messages: StartLlmTestMessage[] = this.llmMessages
      .filter(m => !m.isStreaming)
      .map(m => ({ role: m.role, content: m.content }));

    try {
      // 1) prepare — получаем stream_id ДО старта генерации, чтобы успеть подписаться без race
      const prep = await this.modelsService.apiModelsLlmTestPreparePost().toPromise();
      const streamId = prep?.stream_id;
      if (!streamId) throw new Error('No stream_id from prepare');
      this.currentStreamId = streamId;

      // 2) start — теперь бэк может стримить, чанки дойдут (currentStreamId уже выставлен)
      const command: StartLlmTestCommand = {
        stream_id: streamId,
        llm_version_id: this.selectedTestVersionId,
        messages,
        // Send temperature only when the disable toggle is off; null
        // omits the override on the backend.
        temperature: this.llmTemperatureDisabled ? null : this.llmTemperature,
        // Custom-LLM only — ship the OpenAPI schemas themselves;
        // backend uses each property's `default` value when building
        // the actual outbound request. Empty schemas collapse to null.
        headers: this.allowCustomLlm ? this.composeCustomSchema(this.customHeadersProps) : null,
        body: this.allowCustomLlm ? this.composeCustomSchema(this.customBodyProps) : null,
        // Model parameters defined by the model's additional_properties
        // schema, filled in the tester's Settings accordion.
        additional_properties: this.parameters && Object.keys(this.parameters).length > 0
          ? dictToValueEntries(this.parameters)
          : null
      };
      const result = await this.modelsService.apiModelsLlmTestStartPost({ body: command }).toPromise();

      // Fallback: если Completed не пришёл, но HTTP вернул full_text — финализируем
      const last = this.llmMessages[this.llmMessages.length - 1];
      if (result?.full_text && last?.role === 'assistant' && last.isStreaming) {
        last.content = result.full_text;
        last.isStreaming = false;
        last.promptTokens = result.prompt_tokens;
        last.completionTokens = result.completion_tokens;
        last.tokensPerSec = result.tokens_per_sec;
        last.ttftMs = result.ttft_ms;
        this.llmMessages = [...this.llmMessages];
        this.currentStreamId = null;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    } catch (e) {
      console.error('LLM test failed', e);
      const last = this.llmMessages[this.llmMessages.length - 1];
      if (last && last.role === 'assistant') {
        last.isStreaming = false;
        last.failed = true;
        last.content = $localize`:@@modelTester.llm.error:LLM call failed`;
        this.llmMessages = [...this.llmMessages];
      }
      this.currentStreamId = null;
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  resetLlm(): void {
    this.llmMessages = [];
    this.llmInput = '';
    this.currentStreamId = null;
    this.cdr.markForCheck();
  }

  onLlmInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendLlm();
    }
  }
}
